import { spacingHelper } from '@property.com.au/ui-kit';
import { ResultOf } from 'gql.tada';
import type { FunctionComponent, PropsWithChildren } from 'react';
import styled from 'styled-components';
import { trackDisclaimerModalOpen } from '../../../analytics/event-tracking/trackDisclaimerModalOpen';
import { ContentBrick } from '../../../app-kit/components/ContentBrick/ContentBrick';
import { DisclaimerIcon } from '../../../app-kit/components/DisclaimerIcon/DisclaimerIcon';
import { mapDynamicContentItems } from '../../../app-kit/components/DynamicContent/helpers/mapDynamicContentItem';
import { FeatureItem } from '../../../components/FeatureCollection/FeatureCollection';
import { StyledProptrackLogoBrickSource } from '../../../components/ProptrackLogoBrickSource/ProptrackLogoBrickSource';
import { usePropertyPageModalContext } from '../../contexts/PropertyPageModalContext/PropertyPageModalContext';
import { propertyPagePropertyFeaturesBrickFragment } from '../../graphql/propertyPageQuery';
import { usePropertyPageQuery } from '../../graphql/usePropertyPageQuery';
import { EnergyEfficiencyBrick } from './subcomponents/EnergyEfficiencyBrick/EnergyEfficiencyBrick';
import { PropertySubFeaturesBrick } from './subcomponents/PropertySubFeaturesBrick/PropertySubFeaturesBrick';
import { MieleBannerWithData } from '../MieleBanner/MieleBannerWithData';

type PropertyFeaturesBrickProps = {
  title: string;
  subtitle: string;
};

const PropertyFeaturesBrick: FunctionComponent<PropsWithChildren<PropertyFeaturesBrickProps>> = ({
  title,
  subtitle,
  children,
}) => {
  const { displayModal } = usePropertyPageModalContext();

  return (
    <ContentBrick
      title={title}
      subtitle={subtitle}
      subtitleAs="p"
      headerChildren={
        <DisclaimerIcon
          title="Open Property Features Disclaimer"
          onClick={() => {
            displayModal({ name: 'propertyFeaturesDisclaimer' });
            trackDisclaimerModalOpen({
              inPageSource: 'property_page__property_features_brick___disclaimer_button',
            });
          }}
        />
      }
    >
      {children}
    </ContentBrick>
  );
};

const PropertyFeaturesWrapper = styled.div`
  > :not(:last-child) {
    margin-bottom: ${spacingHelper('small')};
  }
`;

type ApiFeatureItem = NonNullable<
  NonNullable<
    NonNullable<
      ResultOf<typeof propertyPagePropertyFeaturesBrickFragment>['propertyFeaturesV2']
    >['keyFeatures']
  >['itemsV2']
>[number];

const mapPropertyFeatureItems = (propertyFeatureItems: ApiFeatureItem[]): FeatureItem[] =>
  propertyFeatureItems.map((item) => ({ ...item, iconName: item.iconNameV2 }));

export const PropertyFeaturesBrickWithData: FunctionComponent = () => {
  const data = usePropertyPageQuery()?.data.propertyPageBySlugV3;
  const propertyFeatures = data?.propertyFeaturesV2;

  if (!propertyFeatures || (!propertyFeatures.keyFeatures && !propertyFeatures.additionalFeatures))
    return (
      <ContentBrick>
        <MieleBannerWithData />
      </ContentBrick>
    );

  const { title, subtitle, keyFeatures, additionalFeatures } = propertyFeatures;

  const energyEfficiency = data.propertyFeaturesV2?.energyEfficiency;

  return (
    <PropertyFeaturesBrick title={title} subtitle={subtitle}>
      <PropertyFeaturesWrapper>
        {keyFeatures && (
          <PropertySubFeaturesBrick
            title={keyFeatures.title}
            subtitle={keyFeatures.subtitle}
            features={mapPropertyFeatureItems(keyFeatures.itemsV2)}
            updatePropertyDetails={keyFeatures.updatePropertyDetails}
          />
        )}
        <MieleBannerWithData />
        {additionalFeatures && (
          <PropertySubFeaturesBrick
            title={additionalFeatures.title}
            subtitle={additionalFeatures.subtitle}
            features={mapPropertyFeatureItems(additionalFeatures.itemsV2)}
          />
        )}
        {energyEfficiency && (
          <EnergyEfficiencyBrick
            title={energyEfficiency.title}
            subtitle={energyEfficiency.subtitle}
            description={mapDynamicContentItems(energyEfficiency.description)}
            energyEfficiency={energyEfficiency.energyEfficiency}
          />
        )}
      </PropertyFeaturesWrapper>
      <StyledProptrackLogoBrickSource />
    </PropertyFeaturesBrick>
  );
};
